<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import SectionLayout from './SectionLayout.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AccountAPI from 'dashboard/api/account';

const props = defineProps({
  account: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const customRoles = useMapGetter('customRole/getCustomRoles');

const restrictionEnabled = ref(false);
const allowedAgentIds = ref([]);
const allowedTeamIds = ref([]);
const isSaving = ref(false);
const roleKeys = ['administrator', 'supervisor', 'agent'];
const actionKeys = [
  'assign_agent',
  'unassign_agent',
  'self_assign',
  'assign_team',
  'unassign_team',
];
const defaultRolePermissions = () =>
  Object.fromEntries(
    roleKeys.map(role => [
      role,
      Object.fromEntries(actionKeys.map(action => [action, true])),
    ])
  );
const rolePermissions = ref(defaultRolePermissions());
const defaultTeamScopes = () =>
  Object.fromEntries(
    roleKeys.map(role => [role, { mode: 'all', team_ids: [] }])
  );
const roleTeamScopes = ref(defaultTeamScopes());
const userPermissions = ref({});
const teamPermissions = ref({});
const customRolePermissions = ref({});
const defaultOverridePermissions = () =>
  Object.fromEntries(actionKeys.map(action => [action, false]));
const overrideType = ref('user');
const overrideTargetId = ref(null);
const previewTargetId = ref(null);
const preview = ref(null);
const isPreviewLoading = ref(false);
const roles = computed(() => [
  {
    key: 'administrator',
    label: t(
      'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.ROLES.ADMINISTRATOR'
    ),
  },
  {
    key: 'supervisor',
    label: t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.ROLES.SUPERVISOR'),
  },
  {
    key: 'agent',
    label: t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.ROLES.AGENT'),
  },
]);
const actions = computed(() => [
  {
    key: 'assign_agent',
    label: t(
      'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.ACTIONS.ASSIGN_AGENT'
    ),
  },
  {
    key: 'unassign_agent',
    label: t(
      'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.ACTIONS.UNASSIGN_AGENT'
    ),
  },
  {
    key: 'self_assign',
    label: t(
      'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.ACTIONS.SELF_ASSIGN'
    ),
  },
  {
    key: 'assign_team',
    label: t(
      'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.ACTIONS.ASSIGN_TEAM'
    ),
  },
  {
    key: 'unassign_team',
    label: t(
      'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.ACTIONS.UNASSIGN_TEAM'
    ),
  },
]);
const overrideTargets = computed(() => {
  if (overrideType.value === 'team') return teams.value;
  if (overrideType.value === 'custom_role') return customRoles.value;
  return agents.value.filter(agent => !agent.is_bot);
});
const overrideStore = computed(() => {
  if (overrideType.value === 'team') return teamPermissions.value;
  if (overrideType.value === 'custom_role') return customRolePermissions.value;
  return userPermissions.value;
});
const selectedOverride = computed(() => {
  if (!overrideTargetId.value) return null;
  return overrideStore.value[overrideTargetId.value] || null;
});

const normalizeIds = ids =>
  Array.isArray(ids)
    ? ids.map(Number).filter(id => Number.isInteger(id) && id > 0)
    : [];

const initialize = () => {
  const settings = props.account.settings || {};
  restrictionEnabled.value = Boolean(
    settings.conversation_assignment_restriction_enabled
  );
  allowedAgentIds.value = normalizeIds(
    settings.conversation_assignment_allowed_user_ids
  );
  allowedTeamIds.value = normalizeIds(
    settings.conversation_assignment_allowed_team_ids
  );
  const defaults = defaultRolePermissions();
  const configured = settings.conversation_assignment_role_permissions || {};
  rolePermissions.value = Object.fromEntries(
    roleKeys.map(role => [
      role,
      { ...defaults[role], ...(configured[role] || {}) },
    ])
  );
  const configuredScopes =
    settings.conversation_assignment_role_team_scopes || {};
  const scopeDefaults = defaultTeamScopes();
  roleTeamScopes.value = Object.fromEntries(
    roleKeys.map(role => [
      role,
      {
        ...scopeDefaults[role],
        ...(configuredScopes[role] || {}),
        team_ids: normalizeIds(configuredScopes[role]?.team_ids),
      },
    ])
  );
  userPermissions.value =
    settings.conversation_assignment_user_permissions || {};
  teamPermissions.value =
    settings.conversation_assignment_team_permissions || {};
  customRolePermissions.value =
    settings.conversation_assignment_custom_role_permissions || {};
};

const allowedAgentNames = computed(() =>
  allowedAgentIds.value.map(
    id => agents.value.find(agent => agent.id === id)?.name || ''
  )
);

const allowedTeamNames = computed(() =>
  allowedTeamIds.value.map(
    id => teams.value.find(team => team.id === id)?.name || ''
  )
);

const agentMenuItems = computed(() =>
  agents.value
    .filter(
      agent =>
        agent.role === 'agent' &&
        !agent.is_bot &&
        !allowedAgentIds.value.includes(agent.id)
    )
    .map(agent => ({
      action: 'select',
      value: agent.id,
      label: agent.name,
    }))
);

const teamMenuItems = computed(() =>
  teams.value
    .filter(team => !allowedTeamIds.value.includes(team.id))
    .map(team => ({
      action: 'select',
      value: team.id,
      label: team.name,
    }))
);

const addAgent = ({ value }) => {
  if (!allowedAgentIds.value.includes(value)) allowedAgentIds.value.push(value);
};

const removeAgent = index => allowedAgentIds.value.splice(index, 1);

const addTeam = ({ value }) => {
  if (!allowedTeamIds.value.includes(value)) allowedTeamIds.value.push(value);
};

const removeTeam = index => allowedTeamIds.value.splice(index, 1);

const roleScopeTeamNames = role =>
  roleTeamScopes.value[role].team_ids.map(
    id => teams.value.find(team => team.id === id)?.name || ''
  );

const roleScopeTeamMenuItems = role =>
  teams.value
    .filter(team => !roleTeamScopes.value[role].team_ids.includes(team.id))
    .map(team => ({ action: 'select', value: team.id, label: team.name }));

const addRoleScopeTeam = (role, { value }) => {
  if (!roleTeamScopes.value[role].team_ids.includes(value)) {
    roleTeamScopes.value[role].team_ids.push(value);
  }
};

const removeRoleScopeTeam = (role, index) =>
  roleTeamScopes.value[role].team_ids.splice(index, 1);

const selectOverrideTarget = () => {
  if (!overrideTargetId.value) return;
  const permissions = overrideStore.value;
  if (!permissions[overrideTargetId.value]) {
    permissions[overrideTargetId.value] = defaultOverridePermissions();
  }
};

const removeOverride = () => {
  if (!overrideTargetId.value) return;
  delete overrideStore.value[overrideTargetId.value];
  overrideTargetId.value = null;
};

const changeOverrideType = () => {
  overrideTargetId.value = null;
};

const loadPreview = async () => {
  if (!previewTargetId.value) {
    preview.value = null;
    return;
  }

  isPreviewLoading.value = true;
  try {
    const response = await AccountAPI.getConversationAssignmentPreview(
      props.account.id,
      previewTargetId.value
    );
    preview.value = response.data;
  } finally {
    isPreviewLoading.value = false;
  }
};

const previewScopeLabel = computed(() => {
  if (!preview.value) return '';
  switch (preview.value.team_scope.mode) {
    case 'member':
      return t(
        'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE.MEMBER'
      );
    case 'selected':
      return t(
        'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE.SELECTED'
      );
    default:
      return t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE.ALL');
  }
});

const save = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('accounts/update', {
      conversation_assignment_restriction_enabled: restrictionEnabled.value,
      conversation_assignment_allowed_user_ids: allowedAgentIds.value,
      conversation_assignment_allowed_team_ids: allowedTeamIds.value,
      conversation_assignment_role_permissions: rolePermissions.value,
      conversation_assignment_role_team_scopes: roleTeamScopes.value,
      conversation_assignment_user_permissions: userPermissions.value,
      conversation_assignment_team_permissions: teamPermissions.value,
      conversation_assignment_custom_role_permissions:
        customRolePermissions.value,
    });
    await store.dispatch('setUser');
    useAlert(t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

watch(() => props.account.id, initialize);

onMounted(() => {
  initialize();
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('customRole/getCustomRole');
});
</script>

<template>
  <SectionLayout
    :title="$t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TITLE')"
    :description="$t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.NOTE')"
    with-border
  >
    <div class="mb-6 overflow-x-auto rounded-xl border border-n-weak">
      <table class="w-full min-w-[40rem] border-collapse text-sm">
        <thead class="bg-n-alpha-2 text-n-slate-11">
          <tr>
            <th class="px-4 py-3 text-start font-medium">
              {{
                $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.CAPABILITY')
              }}
            </th>
            <th
              v-for="role in roles"
              :key="role.key"
              class="px-4 py-3 text-center font-medium"
            >
              {{ role.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="action in actions"
            :key="action.key"
            class="border-t border-n-weak"
          >
            <td class="px-4 py-3 text-n-slate-12">
              {{ action.label }}
            </td>
            <td
              v-for="role in roles"
              :key="`${role.key}-${action.key}`"
              class="px-4 py-3 text-center"
            >
              <input
                v-model="rolePermissions[role.key][action.key]"
                type="checkbox"
                class="size-4 rounded border-n-strong text-n-brand"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <p
        class="m-0 border-t border-n-weak px-4 py-3 text-body-small text-n-slate-11"
      >
        {{
          $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE_HELP')
        }}
      </p>
    </div>

    <div class="mb-6 rounded-xl border border-n-weak p-4">
      <p class="m-0 text-heading-3 text-n-slate-12">
        {{
          $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE.TITLE')
        }}
      </p>
      <p class="mb-4 mt-1 text-body-small text-n-slate-11">
        {{
          $t(
            'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE.DESCRIPTION'
          )
        }}
      </p>
      <div class="grid gap-4">
        <div v-for="role in roles" :key="role.key" class="grid gap-2">
          <label class="text-body-main font-medium text-n-slate-12">
            {{ role.label }}
          </label>
          <select
            v-model="roleTeamScopes[role.key].mode"
            class="m-0 w-full rounded-lg border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          >
            <option value="all">
              {{
                $t(
                  'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE.ALL'
                )
              }}
            </option>
            <option value="member">
              {{
                $t(
                  'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE.MEMBER'
                )
              }}
            </option>
            <option value="selected">
              {{
                $t(
                  'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE.SELECTED'
                )
              }}
            </option>
          </select>
          <div
            v-if="roleTeamScopes[role.key].mode === 'selected'"
            class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak px-2 py-2"
          >
            <TagInput
              :model-value="roleScopeTeamNames(role.key)"
              :placeholder="
                $t(
                  'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAM_SCOPE.PLACEHOLDER'
                )
              "
              :menu-items="roleScopeTeamMenuItems(role.key)"
              show-dropdown
              skip-label-dedup
              :auto-open-dropdown="false"
              @add="addRoleScopeTeam(role.key, $event)"
              @remove="removeRoleScopeTeam(role.key, $event)"
            />
          </div>
        </div>
      </div>
    </div>

    <details class="mb-6 rounded-xl border border-n-weak p-4">
      <summary class="cursor-pointer text-heading-3 text-n-slate-12">
        {{
          $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.OVERRIDES.TITLE')
        }}
      </summary>
      <p class="mb-4 mt-2 text-body-small text-n-slate-11">
        {{
          $t(
            'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.OVERRIDES.DESCRIPTION'
          )
        }}
      </p>
      <div class="grid gap-3 sm:grid-cols-2">
        <select
          v-model="overrideType"
          class="m-0 rounded-lg border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          @change="changeOverrideType"
        >
          <option value="user">
            {{
              $t(
                'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.OVERRIDES.AGENT'
              )
            }}
          </option>
          <option value="team">
            {{
              $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.OVERRIDES.TEAM')
            }}
          </option>
          <option v-if="customRoles.length" value="custom_role">
            {{
              $t(
                'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.OVERRIDES.CUSTOM_ROLE'
              )
            }}
          </option>
        </select>
        <select
          v-model="overrideTargetId"
          class="m-0 rounded-lg border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
          @change="selectOverrideTarget"
        >
          <option :value="null">
            {{
              $t(
                'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.OVERRIDES.SELECT'
              )
            }}
          </option>
          <option
            v-for="target in overrideTargets"
            :key="target.id"
            :value="target.id"
          >
            {{ target.name }}
          </option>
        </select>
      </div>
      <div
        v-if="selectedOverride"
        class="mt-4 overflow-x-auto rounded-lg border border-n-weak"
      >
        <table class="w-full min-w-[30rem] text-sm">
          <tbody>
            <tr
              v-for="action in actions"
              :key="action.key"
              class="border-t first:border-t-0 border-n-weak"
            >
              <td class="px-4 py-3 text-n-slate-12">{{ action.label }}</td>
              <td class="px-4 py-3 text-end">
                <input
                  v-model="selectedOverride[action.key]"
                  type="checkbox"
                  class="size-4 rounded border-n-strong text-n-brand"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div class="flex justify-end border-t border-n-weak p-3">
          <NextButton
            sm
            slate
            :label="
              $t(
                'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.OVERRIDES.REMOVE'
              )
            "
            @click="removeOverride"
          />
        </div>
      </div>
    </details>

    <div class="mb-6 rounded-xl border border-n-weak p-4">
      <p class="m-0 text-heading-3 text-n-slate-12">
        {{
          $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.SIMULATOR.TITLE')
        }}
      </p>
      <p class="mb-4 mt-1 text-body-small text-n-slate-11">
        {{
          $t(
            'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.SIMULATOR.DESCRIPTION'
          )
        }}
      </p>
      <select
        v-model="previewTargetId"
        class="m-0 w-full rounded-lg border-n-weak bg-n-solid-2 text-sm text-n-slate-12"
        @change="loadPreview"
      >
        <option :value="null">
          {{
            $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.SIMULATOR.SELECT')
          }}
        </option>
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
      <div v-if="isPreviewLoading" class="mt-3 text-body-small text-n-slate-11">
        {{
          $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.SIMULATOR.LOADING')
        }}
      </div>
      <div v-else-if="preview" class="mt-4 rounded-lg border border-n-weak">
        <div
          v-for="action in actions"
          :key="action.key"
          class="flex items-center justify-between border-t first:border-t-0 border-n-weak px-4 py-3 text-sm"
        >
          <span class="text-n-slate-12">{{ action.label }}</span>
          <span
            :class="
              preview.permissions[action.key]
                ? 'text-n-positive'
                : 'text-n-ruby-9'
            "
          >
            {{
              preview.permissions[action.key]
                ? $t(
                    'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.SIMULATOR.ALLOWED'
                  )
                : $t(
                    'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.SIMULATOR.BLOCKED'
                  )
            }}
          </span>
        </div>
        <div
          class="border-t border-n-weak px-4 py-3 text-body-small text-n-slate-11"
        >
          {{
            $t(
              'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.SIMULATOR.TEAM_SCOPE',
              { scope: previewScopeLabel }
            )
          }}
        </div>
      </div>
    </div>

    <SettingsToggleSection
      v-model="restrictionEnabled"
      :header="$t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.ENABLE')"
      :description="$t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.HELP')"
    >
      <template #editor>
        <div class="grid gap-4 pt-2">
          <div>
            <label class="text-heading-3 text-n-slate-12">
              {{ $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.AGENTS') }}
            </label>
            <div
              class="mt-2 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
            >
              <TagInput
                :model-value="allowedAgentNames"
                :placeholder="
                  $t(
                    'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.AGENTS_PLACEHOLDER'
                  )
                "
                :menu-items="agentMenuItems"
                show-dropdown
                skip-label-dedup
                :auto-open-dropdown="false"
                @add="addAgent"
                @remove="removeAgent"
              />
            </div>
          </div>

          <div>
            <label class="text-heading-3 text-n-slate-12">
              {{ $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAMS') }}
            </label>
            <div
              class="mt-2 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
            >
              <TagInput
                :model-value="allowedTeamNames"
                :placeholder="
                  $t(
                    'GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.TEAMS_PLACEHOLDER'
                  )
                "
                :menu-items="teamMenuItems"
                show-dropdown
                skip-label-dedup
                :auto-open-dropdown="false"
                @add="addTeam"
                @remove="removeTeam"
              />
            </div>
          </div>

          <p class="text-body-main text-n-slate-11">
            {{ $t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.BYPASS') }}
          </p>
        </div>
      </template>
    </SettingsToggleSection>
    <div class="mt-6 flex justify-end">
      <NextButton
        :label="$t('GENERAL_SETTINGS.FORM.CONVERSATION_ASSIGNMENT.SAVE')"
        :is-loading="isSaving"
        @click="save"
      />
    </div>
  </SectionLayout>
</template>
